import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import styled from 'styled-components';
import { cardImageUrl } from '../services/helpers';
import { EXPANSION_NAMES } from '../constants';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(0, 0, 0, .78);
`;

const Figure = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-height: 100%;

  & img {
    width: min(90vw, 420px, calc((100vh - 120px) * 600 / 825));
    border-radius: calc(min(90vw, 420px) * .051);
    box-shadow: 0 12px 40px rgba(0, 0, 0, .6);
  }

  & figcaption {
    text-align: center;
    color: #f4f1e8;
    font-size: 14px;
  }
`;

/** Escape closes what's shown full size. */
function useEscape(onClose) {
  useEffect(() => {
    const close = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
}

/**
 * Any picture at full size over everything (a deck's box); a tap anywhere or Escape closes it. Given `onPrev` and
 * `onNext`, it steps like CardZoom (a box's front and back).
 */
export const ImageZoom = ({ src, caption, onClose, onPrev, onNext }) => {
  useEscape(onClose);
  const { swipe, buttons } = useSteps(onPrev, onNext);
  return (
    <Backdrop onClick={onClose} role="dialog" aria-label={caption} {...swipe}>
      {buttons}
      <BoxFigure>
        <img src={src} alt={caption} />
        <figcaption><strong>{caption}</strong></figcaption>
      </BoxFigure>
    </Backdrop>
  );
};

ImageZoom.propTypes = {
  src: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
};

/** A box is any shape: as big as fits. */
const BoxFigure = styled(Figure)`
  & img {
    width: auto;
    max-width: 92vw;
    max-height: calc(100vh - 120px);
    border-radius: 8px;
  }
`;

/** A swipe this far across is a step to the next or previous card. */
const SWIPE_PX = 50;

const Step = styled.button`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 6px;
  transform: translateY(-50%);
  width: 44px;
  height: 64px;
  border: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, .14);
  color: #f4f1e8;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: .2;
    cursor: default;
  }
`;

/**
 * Stepping through what's shown full size: the arrow keys and a sideways swipe go to the previous or next one, when
 * there is one (`onPrev` or `onNext`, null at either end; both left out for one on its own).
 *
 * @returns {{ stepping: boolean, swipe: object, buttons: ?object }} props for the backdrop, and the ‹ › buttons
 */
function useSteps(onPrev, onNext) {
  const stepping = onPrev !== undefined || onNext !== undefined;

  useEffect(() => {
    if (!stepping) {
      return undefined;
    }
    const step = (event) => {
      if (event.key === 'ArrowLeft' && onPrev) {
        onPrev();
      } else if (event.key === 'ArrowRight' && onNext) {
        onNext();
      }
    };
    window.addEventListener('keydown', step);
    return () => window.removeEventListener('keydown', step);
  }, [stepping, onPrev, onNext]);

  const swipeFrom = useRef(null);
  const swipe = stepping ? {
    onTouchStart: (event) => { swipeFrom.current = event.touches[0].clientX; },
    onTouchEnd: (event) => {
      const dx = event.changedTouches[0].clientX - (swipeFrom.current ?? event.changedTouches[0].clientX);
      swipeFrom.current = null;
      if (dx > SWIPE_PX && onPrev) {
        onPrev();
      } else if (dx < -SWIPE_PX && onNext) {
        onNext();
      }
    },
  } : {};
  const stop = (go) => (event) => {
    event.stopPropagation();
    if (go) {
      go();
    }
  };
  const buttons = stepping ? (
    <>
      <Step $side="left" onClick={stop(onPrev)} disabled={!onPrev} aria-label="Previous">‹</Step>
      <Step $side="right" onClick={stop(onNext)} disabled={!onNext} aria-label="Next">›</Step>
    </>
  ) : null;

  return { swipe, buttons };
}

/**
 * A card at full size over everything, to read it; a tap anywhere or Escape closes it. Given `onPrev` and `onNext`
 * (null at either end), the arrow keys, a sideways swipe or the ‹ › buttons step through the cards it was opened from.
 */
const CardZoom = ({ card, onClose, onPrev, onNext }) => {
  useEscape(onClose);
  const { swipe, buttons } = useSteps(onPrev, onNext);

  return (
    <Backdrop onClick={onClose} role="dialog" aria-label={card.get('name')} {...swipe}>
      {buttons}
      <Figure>
        <img src={cardImageUrl(card, { full: true })} alt={card.get('name')} />
        <figcaption>
          <strong>{card.get('name')}</strong>
          {card.get('expansion_id') && <> · {EXPANSION_NAMES[card.get('expansion_id')] || card.get('expansion_id')} #{card.get('card_number')}</>}
          {card.get('enabled') === false && <><br />Not playable yet</>}
        </figcaption>
      </Figure>
    </Backdrop>
  );
};

CardZoom.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
  onClose: PropTypes.func.isRequired,
  /** Steps to the previous card; null at the first. Leave both out for a card on its own. */
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
};

/**
 * Stepping through a list of cards in the full-size view: which one is open (by its place in the list), and the
 * props for CardZoom.
 *
 * @param {Map[]} cards the list, in the order shown
 * @returns {{ open: function(number): void, zoom: ?object }} open(i) shows the i-th; zoom is CardZoom's props, or null
 */
export function useCardSteps(cards, onClose = null) {
  const [index, setIndex] = useState(null);
  const shown = index === null ? null : cards[index];
  return {
    open: setIndex,
    zoom: shown ? {
      card: shown,
      onClose: () => { setIndex(null); if (onClose) onClose(); },
      onPrev: index > 0 ? () => setIndex(index - 1) : null,
      onNext: index < cards.length - 1 ? () => setIndex(index + 1) : null,
    } : null,
  };
}

export default CardZoom;
